import {
  createDefaultViewDefinition,
  type SavedView,
  type SavedViewDefinition,
  type TrackerRecord,
  type TrackerSyncStatus,
} from '@nimbalyst/collab-bundle/trackers-ui';

/**
 * The browser-only "Recent activity" view.
 *
 * It stands in for desktop's personal recents, which ride the personal lane and
 * are structurally unavailable here. Team recency is not a substitute dressed up
 * as the same thing -- it answers a different and equally useful question ("what
 * has the team touched?") from `system.updatedAt` and `system.lastModifiedBy`,
 * both of which already ride the item lane. So this is a client-side sort over
 * rows this tab already holds, not new server data, and it is not added to
 * desktop, where the personal answer exists and is better.
 */
export const RECENT_ACTIVITY_VIEW_ID = 'browser-builtin:recent-activity';

export function createRecentActivitySavedView(): SavedView {
  return {
    id: RECENT_ACTIVITY_VIEW_ID,
    name: 'Recent activity',
    builtIn: true,
    definition: {
      ...createDefaultViewDefinition(),
      selectedType: 'all',
      viewMode: 'list',
      statusScope: 'all',
    },
  };
}

/**
 * Descending team recency.
 *
 * Applied here rather than through the view definition because the shared row
 * ordering (`sortBoardColumnItems`) only sorts ascending, and a "Recent
 * activity" view whose oldest item is at the top is worse than no view.
 */
export function orderByTeamRecency(rows: readonly TrackerRecord[]): TrackerRecord[] {
  return [...rows].sort((left, right) => (
    Date.parse(right.system.updatedAt ?? '') - Date.parse(left.system.updatedAt ?? '')
  ));
}

/**
 * Whether the tracker the URL names is something this tab can still draw.
 *
 * Three different situations produce the same empty grid, and rendering all
 * three as "no rows" is how a surface lies: the room may not have answered yet,
 * the tracker may have been unshared out from under an open tab, or this tab
 * may simply be offline and unable to say either way. The reader's next move is
 * different in each case -- wait, close the tab, or check the connection -- so
 * the cases are kept apart here rather than in JSX where they would collapse
 * into one ternary.
 */
export type TrackerTypeAvailability = 'available' | 'pending' | 'unshared' | 'indeterminate';

export function resolveTrackerTypeAvailability({
  selectedType,
  knownTypes,
  loaded,
  syncStatus,
}: {
  selectedType: string;
  /** Tracker types the synced schema knows about, builtins included. */
  knownTypes: readonly string[];
  /** False until the first snapshot resolves. */
  loaded: boolean;
  syncStatus: TrackerSyncStatus;
}): TrackerTypeAvailability {
  if (selectedType === 'all') return 'available';
  if (knownTypes.includes(selectedType)) return 'available';
  // The engine finishes its schema bootstrap before it reports `connected`, so
  // any earlier status means the schema is still arriving and a missing type
  // says nothing yet.
  if (!loaded || syncStatus === 'connecting' || syncStatus === 'syncing') return 'pending';
  if (syncStatus === 'connected') return 'unshared';
  return 'indeterminate';
}

export interface TrackerViewSelection {
  /** The view whose definition is in force, or null when a bare type is selected. */
  activeSavedViewId: string | null;
  /** `'all'` or a tracker type; what the type tree highlights. */
  selectedType: string;
  definition: SavedViewDefinition;
}

/**
 * Resolve what the surface should show from the URL alone.
 *
 * The two axes are deliberately different kinds of thing. A **type** is a place
 * (a path segment); a **saved view** is a lens (`?view=`), and it carries its
 * own type selection, so applying one moves the type highlight with it. A view
 * id that no longer resolves -- an unshared view, a link from a teammate whose
 * view was deleted -- falls back to the plain type rather than rendering an
 * empty surface that looks like a sync failure.
 */
export function resolveTrackerViewSelection({
  trackerType,
  viewId,
  savedViews,
}: {
  trackerType?: string | null;
  viewId?: string | null;
  savedViews: readonly SavedView[];
}): TrackerViewSelection {
  const view = viewId ? savedViews.find((candidate) => candidate.id === viewId) ?? null : null;
  if (view) {
    return {
      activeSavedViewId: view.id,
      selectedType: view.definition.selectedType,
      definition: view.definition,
    };
  }
  const selectedType = trackerType || 'all';
  return {
    activeSavedViewId: null,
    selectedType,
    // The table, not the shared default's compact list: the desktop opens a
    // tracker on its row table, and the browser's list mode is a different,
    // sparser component, so `table` is the view that reads the same in both.
    definition: { ...createDefaultViewDefinition(), selectedType, statusScope: 'open', viewMode: 'table' },
  };
}
